import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase-client';
import TextInput from './formComponents/TextInput';
import SubmitButton from './formComponents/SubmitButton';
import ErrorMessage from './formComponents/ErrorMessage';
import ParentEmergencySyncDetailsModal from './ParentEmergencySyncDetailsModal';

const syncEmergencyContactsFromParentFn = httpsCallable(functions, 'syncEmergencyContactsFromParent');

export default function EmergencyContactForm({
  userId,
  onSaved,
  onCancel,
  hideHeading = false,
  submitLabel = 'Save Emergency Contacts',
  showCancel = false,
}) {
  const [contacts, setContacts] = useState([]);
  const [originalContacts, setOriginalContacts] = useState([]);
  const [autoSyncToChildren, setAutoSyncToChildren] = useState(false);
  const [originalAutoSyncToChildren, setOriginalAutoSyncToChildren] = useState(false);
  const [syncChildCount, setSyncChildCount] = useState(0);
  const [syncFamilyCount, setSyncFamilyCount] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncDetailsModalOpen, setSyncDetailsModalOpen] = useState(false);
  const [pendingAutoSyncEnable, setPendingAutoSyncEnable] = useState(false);
  const [, setPendingContactSyncAfterDetails] = useState(false);

  const clearEmergencyReminderNotifications = async () => {
    const existingQuery = query(
      collection(db, 'notifications', userId, 'items'),
      where('type', '==', 'reminder-emergency-check')
    );
    const existingSnap = await getDocs(existingQuery);
    if (existingSnap.empty) return;

    await Promise.all(
      existingSnap.docs.map((snap) => deleteDoc(snap.ref).catch(() => {}))
    );
  };

  useEffect(() => {
    let cancelled = false;
    const loadContacts = async () => {
      setLoading(true);
      setContacts([]);
      setOriginalContacts([]);
      setAutoSyncToChildren(false);
      setOriginalAutoSyncToChildren(false);
      setSyncChildCount(0);
      setSyncFamilyCount(0);
      setError('');
      setSuccessMessage('');
      setSyncDetailsModalOpen(false);
      setPendingAutoSyncEnable(false);
      setPendingContactSyncAfterDetails(false);

      try {
        const [privateSnap, userSnap] = await Promise.all([
          getDoc(doc(db, 'privateUsers', userId)),
          getDoc(doc(db, 'users', userId)),
        ]);

        if (cancelled) return;

        if (privateSnap.exists()) {
          const privateData = privateSnap.data() || {};
          const loaded = privateData.emergencyContacts || [];
          const cloned = loaded.map(contact => ({ ...contact }));
          setContacts(cloned);
          setOriginalContacts(cloned.map(contact => ({ ...contact })));
          const savedAutoSync = Boolean(privateData.autoSyncFamilyEmergencyContacts);
          setAutoSyncToChildren(savedAutoSync);
          setOriginalAutoSyncToChildren(savedAutoSync);
        }

        if (userSnap.exists()) {
          const roles = userSnap.data()?.family_roles || {};
          const parentFamilyIds = Object.entries(roles)
            .filter(([, role]) => role === 'parent')
            .map(([familyId]) => familyId);

          if (parentFamilyIds.length > 0) {
            const memberSnaps = await Promise.all(
              parentFamilyIds.map((familyId) => getDocs(
                query(
                  collection(db, 'families', familyId, 'members'),
                  where('role', '==', 'child')
                )
              ))
            );

            const childUidSet = new Set();
            memberSnaps.forEach((snap) => {
              snap.forEach((memberDoc) => childUidSet.add(memberDoc.id));
            });

            setSyncFamilyCount(parentFamilyIds.length);
            setSyncChildCount(childUidSet.size);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadContacts();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const updateContact = (index, field, value) => {
    const updated = [...contacts];
    updated[index] = { ...updated[index], [field]: value };
    setContacts(updated);
  };

  const addContact = () => {
    setContacts(prev => [
      ...prev,
      {
        name: '',
        relationship: '',
        phone: '',
        address: '',
        email: '',
        alternatePhone: '',
      },
    ]);
  };

  const removeContact = (index) => {
    const contact = contacts[index];
    if (!window.confirm(`Remove ${contact?.name || `contact ${index + 1}`}? This change is applied when you save.`)) return;
    const updated = [...contacts];
    updated.splice(index, 1);
    setContacts(updated);
  };

  const validateContacts = () => {
    for (let i = 0; i < contacts.length; i++) {
      const c = contacts[i];
      if (!c.name || !c.relationship || !c.phone || !c.address) {
        setError(`Contact ${i + 1} is missing required fields.`);
        return false;
      }
    }
    return true;
  };

  const persistAutoSyncPreference = async (checked) => {
    await setDoc(doc(db, 'privateUsers', userId), {
      autoSyncFamilyEmergencyContacts: checked,
    }, { merge: true });
    setAutoSyncToChildren(checked);
    setOriginalAutoSyncToChildren(checked);
  };

  const ensureSyncProfileDetails = async () => {
    const privateSnap = await getDoc(doc(db, 'privateUsers', userId));
    const privateData = privateSnap.exists() ? (privateSnap.data() || {}) : {};
    const hasPhone = String(privateData?.phone || '').trim() !== '';
    const hasAddress = String(privateData?.address || '').trim() !== '';

    if (hasPhone && hasAddress) return true;

    setSyncDetailsModalOpen(true);
    return false;
  };

  const syncParentProfileToChildren = async ({ nextAutoSync = autoSyncToChildren } = {}) => {
    const syncResult = await syncEmergencyContactsFromParentFn();
    if (
      syncResult?.data?.missingParentPhone === true
      || syncResult?.data?.missingParentAddress === true
      || syncResult?.data?.missingParentContacts === true
    ) {
      throw new Error('Add both a phone number and address in Profile before syncing yourself as the emergency contact for child accounts.');
    }

    const syncedChildren = Number(syncResult?.data?.syncedChildren || 0);
    const failedChildren = Number(syncResult?.data?.failedChildren || 0);

    if (failedChildren > 0) {
      setError(
        `Synced your profile as the emergency contact for ${syncedChildren} child account${syncedChildren === 1 ? '' : 's'}, but ${failedChildren} failed.`
      );
    } else if (nextAutoSync) {
      setSuccessMessage(
        `Your profile was synced as the emergency contact for ${syncedChildren} child account${syncedChildren === 1 ? '' : 's'}.`
      );
    }
  };

  const handleAutoSyncToggle = async (checked) => {
    setError('');
    setSuccessMessage('');
    setSaving(true);

    try {
      if (!checked) {
        await persistAutoSyncPreference(false);
        setPendingAutoSyncEnable(false);
        return;
      }

      setAutoSyncToChildren(true);
      const hasSyncDetails = await ensureSyncProfileDetails();
      if (!hasSyncDetails) {
        setPendingAutoSyncEnable(true);
        return;
      }

      await persistAutoSyncPreference(true);
      if (syncChildCount > 0) {
        await syncParentProfileToChildren({ nextAutoSync: checked });
      }
    } catch (err) {
      setAutoSyncToChildren(originalAutoSyncToChildren);
      setPendingAutoSyncEnable(false);
      setError(err?.message || 'Could not update child account sync.');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncDetailsModalClose = async () => {
    setSyncDetailsModalOpen(false);
    setPendingContactSyncAfterDetails(false);
    if (!pendingAutoSyncEnable) return;

    setSaving(true);
    try {
      await persistAutoSyncPreference(originalAutoSyncToChildren);
    } finally {
      setPendingAutoSyncEnable(false);
      setSaving(false);
    }
  };

  const handleSyncDetailsSaved = async () => {
    setSyncDetailsModalOpen(false);
    setError('');
    setSuccessMessage('');
    setSaving(true);

    try {
      if (pendingAutoSyncEnable) {
        await persistAutoSyncPreference(true);
      }
      await syncParentProfileToChildren({
        nextAutoSync: pendingAutoSyncEnable ? true : autoSyncToChildren,
      });
      setPendingAutoSyncEnable(false);
      setPendingContactSyncAfterDetails(false);
    } catch (err) {
      setError(err?.message || 'Could not sync your profile to child accounts.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccessMessage('');
    if (!validateContacts()) return;
    setSaving(true);

    const privateRef = doc(db, 'privateUsers', userId);
    try {
      await setDoc(privateRef, {
        emergencyContacts: contacts,
        emergencyContactsLastModified: Timestamp.now(),
        emergencyContactsLastReviewed: serverTimestamp(),
        autoSyncFamilyEmergencyContacts: autoSyncToChildren,
      }, { merge: true });
      if (contacts.length > 0) {
        await clearEmergencyReminderNotifications();
      }
    } catch {
      setError('Could not save emergency contact info to your profile.');
      setSaving(false);
      return;
    }

    try {
      const userSnap = await getDoc(doc(db, 'users', userId));
      const teamRoles = userSnap.data().team_roles || {};

      for (const teamId of Object.keys(teamRoles)) {
        try {
          await updateDoc(doc(db, 'teams', teamId, 'members', userId), {
            emergencyContacts: contacts,
          });
        } catch {
          // Silent fail on per-team update
        }
      }
    } catch {
      setError('Could not load your team roles.');
    }

    if (autoSyncToChildren && syncChildCount > 0) {
      try {
        const hasSyncDetails = await ensureSyncProfileDetails();
        if (!hasSyncDetails) {
          setPendingContactSyncAfterDetails(true);
          setOriginalContacts(contacts.map(contact => ({ ...contact })));
          setOriginalAutoSyncToChildren(autoSyncToChildren);
          setSaving(false);
          onSaved?.(contacts.map(contact => ({ ...contact })));
          return;
        }
        await syncParentProfileToChildren({ nextAutoSync: autoSyncToChildren });
      } catch (err) {
        setError(`Saved your emergency contacts, but ${err?.message || 'syncing your profile to child accounts failed.'}`);
      }
    } else {
      setSuccessMessage('Emergency contacts saved successfully.');
    }

    setOriginalContacts(contacts.map(contact => ({ ...contact })));
    setOriginalAutoSyncToChildren(autoSyncToChildren);
    setTimeout(() => setSuccessMessage(''), 3000);
    setSaving(false);
    onSaved?.(contacts.map(contact => ({ ...contact })));
  };

  const handleConfirmReview = async () => {
    setError('');
    setSuccessMessage('');
    if (!validateContacts()) return;

    setSaving(true);
    try {
      await setDoc(doc(db, 'privateUsers', userId), {
        emergencyContactsLastReviewed: serverTimestamp(),
      }, { merge: true });
      await clearEmergencyReminderNotifications();
      setOriginalContacts(contacts.map(contact => ({ ...contact })));
      setOriginalAutoSyncToChildren(autoSyncToChildren);
      setSuccessMessage('Emergency contact review recorded.');
      setTimeout(() => setSuccessMessage(''), 3000);
      onSaved?.(contacts.map(contact => ({ ...contact })));
    } catch (err) {
      setError(err?.message || 'Could not confirm your emergency contact review.');
    } finally {
      setSaving(false);
    }
  };

  const isDirty = (
    JSON.stringify(contacts) !== JSON.stringify(originalContacts)
    || autoSyncToChildren !== originalAutoSyncToChildren
  );
  const canConfirmReview = contacts.length > 0 && !isDirty;

  if (loading) return <p>Loading emergency contacts…</p>;

  return (
    <section>
      {!hideHeading && <h2>Emergency Contact Details</h2>}

      {syncChildCount > 0 && (
        <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoSyncToChildren}
              onChange={(e) => handleAutoSyncToggle(e.target.checked)}
              disabled={saving}
            />
            <span style={{ fontWeight: 600 }}>
              Auto sync my details to child accounts
            </span>
          </label>
          <p style={{ margin: '0.6rem 0 0', color: 'var(--clr-text-muted)' }}>
            Turning this on immediately syncs your profile details as the emergency contact for {syncChildCount} child account
            {syncChildCount === 1 ? '' : 's'} across {syncFamilyCount} famil
            {syncFamilyCount === 1 ? 'y' : 'ies'} where you are a parent.
          </p>
        </div>
      )}

      {contacts.map((contact, index) => (
        <div key={index} className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <h3>Contact {index + 1}</h3>
          <TextInput label="Full Name *" value={contact.name} onChange={e => updateContact(index, 'name', e.target.value)} />
          <TextInput label="Relationship *" value={contact.relationship} onChange={e => updateContact(index, 'relationship', e.target.value)} />
          <TextInput label="Phone Number *" value={contact.phone} onChange={e => updateContact(index, 'phone', e.target.value)} />
          <TextInput label="Address *" value={contact.address} onChange={e => updateContact(index, 'address', e.target.value)} />
          <TextInput label="Email Address" value={contact.email} onChange={e => updateContact(index, 'email', e.target.value)} />
          <TextInput label="Alternate Phone" value={contact.alternatePhone} onChange={e => updateContact(index, 'alternatePhone', e.target.value)} />
          <button
            className="button small"
            onClick={() => removeContact(index)}
            style={{ background: '#ccc', marginTop: '0.5rem' }}
          >
            Remove Contact
          </button>
        </div>
      ))}

      <button className="button" onClick={addContact} style={{ marginBottom: '1rem' }}>
        Add Another Contact
      </button>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {showCancel && (
          <SubmitButton
            type="button"
            variant="ghost"
            label="Cancel"
            onClick={onCancel}
            disabled={saving}
          />
        )}
        <SubmitButton
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          label={saving ? 'Saving…' : isDirty ? submitLabel : 'All Changes Saved'}
        />
        {canConfirmReview && (
          <SubmitButton
            type="button"
            variant="secondary"
            onClick={handleConfirmReview}
            disabled={saving}
            label={saving ? 'Saving…' : 'Confirm Contacts Reviewed'}
          />
        )}
      </div>

      {successMessage && <p style={{ color: 'green', marginTop: '0.5rem' }}>✔ {successMessage}</p>}
      <ErrorMessage message={error} />

      {syncDetailsModalOpen && (
        <ParentEmergencySyncDetailsModal
          userId={userId}
          onClose={handleSyncDetailsModalClose}
          onSaved={handleSyncDetailsSaved}
        />
      )}
    </section>
  );
}
